import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useApp } from '../context/AppContext';

const HomeIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/>
  </svg>
);
const SearchIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
  </svg>
);
const PlusIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
  </svg>
);
const MapIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="3 11 22 2 13 21 11 13 3 11"/>
  </svg>
);
const ChatIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
  </svg>
);
const UserIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
  </svg>
);

const styles = {
  nav: {
    position: 'fixed', bottom: 0, left: 0, right: 0,
    background: 'var(--navy-light)',
    borderTop: '1px solid var(--navy-border)',
    display: 'flex', alignItems: 'stretch',
    zIndex: 100,
    paddingBottom: 'env(safe-area-inset-bottom)',
  },
  item: {
    flex: 1, display: 'flex', flexDirection: 'column',
    alignItems: 'center', justifyContent: 'center',
    padding: '10px 4px 8px',
    color: 'var(--gray-500)',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    transition: 'color 0.2s',
    gap: '3px',
    fontSize: '10px',
    fontWeight: '500',
    letterSpacing: '0.2px',
  },
  itemActive: { color: 'var(--accent)' },
  plusBtn: {
    flex: 1, display: 'flex', flexDirection: 'column',
    alignItems: 'center', justifyContent: 'center',
    padding: '6px 4px 8px',
    background: 'none', border: 'none', cursor: 'pointer',
    gap: '3px', fontSize: '10px', fontWeight: '500',
    color: 'var(--gray-500)',
    transition: 'color 0.2s',
  },
  plusInner: {
    width: 40, height: 40,
    background: 'var(--accent)',
    borderRadius: '50%',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    color: 'white',
    boxShadow: '0 4px 12px rgba(59,130,246,0.4)',
    marginBottom: 2,
  },
  dot: {
    width: 6, height: 6, borderRadius: '50%',
    background: 'var(--red)',
    position: 'absolute', top: 8, right: 8,
  },
};

export default function Navbar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser, getMyConversations } = useApp();

  const path = location.pathname;
  const convs = getMyConversations();
  const hasUnread = convs.length > 0;

  const tabs = [
    { label: 'Home', icon: <HomeIcon />, path: '/dashboard' },
    { label: 'Browse', icon: <SearchIcon />, path: '/browse' },
    { label: 'Post', icon: null, path: '/post-ride' },
    { label: 'My Rides', icon: <MapIcon />, path: '/my-rides' },
    { label: 'Messages', icon: <ChatIcon />, path: '/messages', badge: hasUnread },
  ];

  if (!currentUser) return null;

  return (
    <nav style={styles.nav}>
      {tabs.map((tab) => {
        const isActive = path.startsWith(tab.path);
        if (tab.path === '/post-ride') {
          return (
            <button
              key={tab.path}
              style={{ ...styles.plusBtn, ...(isActive ? { color: 'var(--accent)' } : {}) }}
              onClick={() => navigate(tab.path)}
            >
              <div style={{ ...styles.plusInner, ...(isActive ? { background: 'var(--accent-hover)' } : {}) }}>
                <PlusIcon />
              </div>
              <span>{tab.label}</span>
            </button>
          );
        }
        return (
          <button
            key={tab.path}
            style={{ ...styles.item, ...(isActive ? styles.itemActive : {}) }}
            onClick={() => navigate(tab.path)}
          >
            <div style={{ position: 'relative' }}>
              {tab.icon}
              {tab.badge && <div style={styles.dot} />}
            </div>
            <span>{tab.label}</span>
          </button>
        );
      })}
      <button
        style={{ ...styles.item, ...(path.startsWith('/profile') ? styles.itemActive : {}) }}
        onClick={() => navigate(`/profile/${currentUser.id}`)}
      >
        <UserIcon />
        <span>Profile</span>
      </button>
    </nav>
  );
}
